import type { ViewState } from './types';

/* URL と ViewState の対応表。**ここが唯一の定義**。
 *
 * 以前は App.tsx に
 *   ① navigateTo 内の viewToPath
 *   ② popstate ハンドラ内の pathToView
 *   ③ 初期マウント時の pathToView
 *   ④ checkSession 内の isPublicPage（ハードコード配列）
 * が別々に置かれていた。④に /manager/settings が無く、①〜③にも無かったため、
 * Stripe 決済から `/manager/settings?checkout=success` に戻ると
 *   path が未知 → view='landing' → isPublicPage=false → ダッシュボードにも飛ばない
 * となり、管理画面のURLで公開LPが表示されていた。表を1つにして直す。
 */

export type RouteAccess =
  /** 未ログインでも見せる。ログイン済みで landing/login に来たらダッシュボードへ送る対象。 */
  | 'public'
  /** ログイン必須。セッションが無ければログイン画面へ落とす。 */
  | 'manager';

export interface RouteDef {
  path: string;
  view: ViewState;
  access: RouteAccess;
  /** 配下すべてを同じ view に解決する（例: /posters/H1）。 */
  prefix?: boolean;
}

export const ROUTES: readonly RouteDef[] = [
  { path: '/', view: 'landing', access: 'public' },
  { path: '/about', view: 'about', access: 'public' },
  { path: '/why-demo', view: 'why_demo', access: 'public' },
  /* 登録不要デモへの直リンク。ブログ記事の「試す」系CTAの行き先。
     以前は行き先が `/`（LP）で、そこでヒーローのデモボタンをもう一度押さないと
     デモに入れなかった（sticky CTA は「登録不要」と謳っているのに）。
     ★navigateTo('worker_landing') はどこからも呼ばれていない（遷移は setView）ため、
     この対応表を足しても既存のワーカー画面の URL は変わらない。 */
  { path: '/demo', view: 'worker_landing', access: 'public' },
  { path: '/my-reports', view: 'my_reports_list', access: 'public' },
  { path: '/guide', view: 'guide', access: 'public' },
  { path: '/pricing', view: 'pricing', access: 'public' },
  { path: '/posters', view: 'posters', access: 'public', prefix: true },
  { path: '/legal', view: 'legal', access: 'public' },
  /* 法的表記はタブ切替のページだが、**それぞれ URL を持たせる。**
     特商法とプライバシーポリシーは、決済事業者の審査・クローラ・
     「該当ページを見せてください」という問い合わせのいずれでも
     直リンクを求められる。URL が無いとフッターから辿るしかない。 */
  { path: '/legal/about', view: 'legal_about', access: 'public' },
  { path: '/legal/privacy', view: 'legal_privacy', access: 'public' },
  { path: '/legal/transactions', view: 'legal_transactions', access: 'public' },
  { path: '/legal/contact', view: 'legal_contact', access: 'public' },
  { path: '/login', view: 'login', access: 'public' },
  { path: '/lp/construction', view: 'lp_construction', access: 'public' },
  { path: '/lp/manufacturing', view: 'lp_manufacturing', access: 'public' },
  { path: '/lp/logistics', view: 'lp_logistics', access: 'public' },
  // 決済後の戻り先。ここが欠けていたのが「決済後に公開LPが出る」不具合の原因だった。
  { path: '/manager/settings', view: 'manager_settings', access: 'manager' },
  // 運営者専用。URLを知っていても RPC 側で 42501 になるので、ここに置いても情報は漏れない。
  { path: '/manager/usage', view: 'operator_usage', access: 'manager' },
];

const BY_PATH = new Map(ROUTES.map((r) => [r.path, r]));

/** 末尾スラッシュを落として比較する（/posters/ と /posters を同じに扱う）。 */
function normalize(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.replace(/\/+$/, '');
  return pathname || '/';
}

export function matchRoute(pathname: string): RouteDef | null {
  const path = normalize(pathname);
  const exact = BY_PATH.get(path);
  if (exact) return exact;
  for (const route of ROUTES) {
    if (route.prefix && path.startsWith(`${route.path}/`)) return route;
  }
  return null;
}

/** 未知のパスは landing に落とす（従来挙動を維持）。 */
export function pathToView(pathname: string): ViewState {
  return matchRoute(pathname)?.view ?? 'landing';
}

const VIEW_TO_PATH = new Map<ViewState, string>();
for (const route of ROUTES) {
  if (!VIEW_TO_PATH.has(route.view)) VIEW_TO_PATH.set(route.view, route.path);
}

/** URL を持たない view（legal_* / worker_* など）は '/' 扱い（従来挙動を維持）。 */
export function viewToPath(view: ViewState): string {
  return VIEW_TO_PATH.get(view) ?? '/';
}

export function isPublicPath(pathname: string): boolean {
  return matchRoute(pathname)?.access === 'public';
}

export function requiresAuth(pathname: string): boolean {
  return matchRoute(pathname)?.access === 'manager';
}

/** /posters/H1 → 'H1'。ギャラリーの deep link 解決用。 */
export function posterIdFromPath(pathname: string): string | null {
  const m = normalize(pathname).match(/^\/posters\/([A-Za-z0-9-]{1,12})$/);
  return m ? m[1].toUpperCase() : null;
}
